import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Snackbar, Alert } from "@mui/material";

import HeaderLayout from "../../../components/HeaderLayout";
import InputTextField from "../../../components/InputTextField";
import FormikSubmitButton from "../../../components/FormikSubmitButton";
import api from "../../../api/apiClient";
import PasswordRequirements from "../../../components/PasswordRequirements";

// ✅ Validation Schema
const ChangePasswordSchema = Yup.object({
  oldpassword: Yup.string().required("Old password is required"),
  newpassword: Yup.string()
    .min(6, "New password must be at least 6 characters")
    .required("New password is required"),
  confirmnewpassword: Yup.string()
    .oneOf([Yup.ref("newpassword"), null], "Passwords must match")
    .required("Please confirm your new password"),
});

export default function ChangePassword() {
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success"); // 'success' or 'error'

  return (
    <div className="flex flex-col h-screen mr-2 ml-2">
      <div className="flex-shrink-0">
        <HeaderLayout name="Change Password" />
      </div>

      <Formik
        initialValues={{
          oldpassword: "",
          newpassword: "",
          confirmnewpassword: "",
        }}
        validationSchema={ChangePasswordSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          try {
            const payload = {
              oldPassword: values.oldpassword,
              newPassword: values.newpassword,
              confirmPassword: values.confirmnewpassword,
            };

            const res = await api.post("/user/changePassword", payload);

            // ✅ If backend success = false, treat as error
            if (!res.data.success) {
              throw new Error(res.data.message || "Failed to change password");
            }

            // ✅ Success
            setToastType("success");
            setToastMessage("Password changed successfully!");
            setToastOpen(true);
            resetForm();
          } catch (error) {
            console.error("Error changing password:", error);

            // ✅ Extract clean error message
            const message =
              error.response?.data?.message || error.message || "Server error";

            setToastType("error");
            setToastMessage(message);
            setToastOpen(true);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form className="flex flex-col flex-1">
            <div className="overflow-y-auto h-[62vh] px-5 py-6 space-y-10 hide-scrollbar">
              <div className="grid grid-cols-2 gap-12">
                <InputTextField
                  label="Old Password"
                  name="oldpassword"
                  placeholder="Enter old password"
                  type="password"
                  requiredMark
                  hiddenPassword
                />
                <div className="space-y-2">
                  <InputTextField
                    label="New Password"
                    name="newpassword"
                    placeholder="Enter new password"
                    type="password"
                    requiredMark
                    hiddenPassword
                  />
                  <PasswordRequirements name="newpassword" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-12">
                <InputTextField
                  label="Confirm New Password"
                  name="confirmnewpassword"
                  placeholder="Confirm new password"
                  type="password"
                  requiredMark
                  hiddenPassword
                />
              </div>
            </div>

            <div className="flex-shrink-0 flex items-center gap-4 justify-center pt-4 border-t bg-white">
              <FormikSubmitButton
                name={isSubmitting ? "Submitting..." : "Submit"}
                disabled={isSubmitting}
                className="flex items-center justify-center border py-3 px-6 
                             cursor-pointer text-white rounded bg-blue-600 
                             hover:bg-blue-700 transition-colors w-[90px] h-[44px]"
              />
            </div>
          </Form>
        )}
      </Formik>

      {/* ✅ Success/Error Toast */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={2500}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastType}
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}
